import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { Button } from '@/design-system/Button'
import { apiClient, ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { accessToken } from '@/lib/staffSession'

import type { MediaAsset } from '../types'
import type { Page } from '../useCollection'

/**
 * Choisir ou téléverser une image.
 *
 * Le champ montre **l'image**, pas son identifiant. Un `media_id` en clair
 * dans un formulaire n'apprend rien à personne : on veut voir la photo du
 * plat qu'on est en train de décrire.
 *
 * Les images déjà téléversées restent proposées : une même photo sert souvent
 * à plusieurs endroits, et la reverser à chaque fois remplirait le disque de
 * doublons.
 */
export function MediaField({
  label,
  hint,
  value,
  eventId,
  onChange,
  aspect = 'square',
}: {
  label: string
  hint?: string
  value: string | null
  eventId: string | undefined
  onChange: (mediaId: string | null) => void
  /** `wide` pour un visuel de bandeau, `square` pour une vignette. */
  aspect?: 'square' | 'wide'
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [browsing, setBrowsing] = useState(false)

  const library = useQuery({
    queryKey: ['admin-media', eventId],
    queryFn: async () =>
      apiClient.get<Page<MediaAsset>>('/admin/media?page_size=200', {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId),
  })

  const assets = library.data?.items ?? []
  const current = assets.find((asset) => asset.id === value) ?? null

  async function upload(file: File) {
    setBusy(true)
    setError(null)
    try {
      // `FormData` et non JSON : c'est un fichier. Le client ne pose pas de
      // `Content-Type`, le navigateur ajoute lui-même la frontière multipart.
      const form = new FormData()
      form.append('file', file)
      if (eventId) form.append('event_id', eventId)

      const created = await apiClient.post<MediaAsset>('/admin/media', form, {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      })
      await queryClient.invalidateQueries({ queryKey: ['admin-media', eventId] })
      onChange(created.id)
      setBrowsing(false)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('admin.media.failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <span className="text-caption uppercase tracking-wide text-content-muted">{label}</span>

      <div className="mt-1.5 flex items-start gap-4">
        <div
          className={cn(
            'shrink-0 overflow-hidden rounded-md border border-line bg-bg-base',
            aspect === 'wide' ? 'h-20 w-36' : 'h-20 w-20',
          )}
        >
          {current ? (
            <img
              src={current.url}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-xs text-content-muted">
              {t('admin.media.none')}
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-wrap gap-2">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/avif"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              // Le champ est vidé pour que reprendre le même fichier après une
              // erreur déclenche bien un nouvel envoi.
              event.target.value = ''
              if (file) void upload(file)
            }}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy ? t('state.loading') : t('admin.media.upload')}
          </Button>
          {assets.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => setBrowsing((open) => !open)}>
              {t('admin.media.choose')}
            </Button>
          ) : null}
          {value ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => onChange(null)}>
              {t('admin.media.remove')}
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <p className="mt-2 text-sm text-state-danger">{error}</p> : null}
      {hint ? <p className="mt-1.5 text-xs text-content-muted">{hint}</p> : null}

      {browsing ? (
        <ul className="mt-3 grid max-h-56 grid-cols-4 gap-2 overflow-y-auto rounded-md border border-line-subtle p-2 sm:grid-cols-6">
          {assets.map((asset) => (
            <li key={asset.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(asset.id)
                  setBrowsing(false)
                }}
                aria-current={asset.id === value}
                className={cn(
                  'block aspect-square w-full overflow-hidden rounded border transition',
                  asset.id === value
                    ? 'border-champagne-400'
                    : 'border-line-subtle hover:border-champagne-400/60',
                )}
              >
                <img
                  src={asset.url}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
